import { ConnectAccount } from "./connect";
import { distributionPlanState, planSubmitted } from "~/common/publishing/distribution-plan-state";
import { DistributionBackContext } from "~/common/publishing/distribution-panel-frame";
import { visibleDraftIssues } from "~/common/publishing/draft-issues";
import { fieldLength, fieldRequired, type Field } from "@ondit/publishing";
import {
	Button,
	ChipConfirmModal,
	ChipModal,
	ChipModalBody,
	ChipModalFooter,
	ChipModalHeader,
	Input,
	Label,
	NativeSelect,
	Textarea,
} from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, RefreshCw, Trash2 } from "lucide-react";
import { type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ArticleSheet } from "~/common/article/writing-field";
import { PublishingField } from "~/common/publishing/publishing-field";
import { fileUrl } from "~/common/media/api";
import { ChannelPicker, type ChannelSelection } from "~/common/publishing/channel-picker";
import { SourceMediaField } from "~/common/publishing/source-media-field";
import { SourceImageField } from "~/common/publishing/source-image-field";
import { PlatformPreview } from "~/common/publishing/platform-preview";
import { useAccess } from "~/core/access/index";
import { ApiError, api } from "~/core/api/index";
import { type DistributionBrief, type DistributionDraft, smartDistribution } from "~/core/api/smart-distribution";
import { dateTime, language as languageName } from "~/core/format";
import { lookup, pick, useI18n, useT } from "~/core/i18n";
import { SearchSelect } from "~/core/ui/search-select";
import "./publishing.css";
import "./smart-distribution.css";
import { localDateTime } from "./calendar-data";

export function SmartDistribution({
	entityId,
	pageId,
	sourceMaterial,
	sourceControl,
	disabledRecommendation,
	onEditMaterial,
	onPlanChange,
}: {
	entityId: string;
	pageId: string;
	sourceMaterial?: { title: string; locale: string };
	sourceControl?: ReactNode;
	disabledRecommendation?: boolean;
	onEditMaterial?: () => void;
	onPlanChange?: (open: boolean) => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const [params] = useSearchParams();
	const [language, setLanguage] = useState<string>("");
	const [min, setMin] = useState(3);
	const [max, setMax] = useState(5);
	const [extra, setExtra] = useState<string[]>([]);
	const [adding, setAdding] = useState("");
	const [brief, setBrief] = useState<DistributionBrief | null>(null);
	const [selected, setSelected] = useState<string[]>([]);
	const [selecting, setSelecting] = useState(false);
	const [drafts, setDrafts] = useState<DistributionDraft[]>([]);
	const [requestBusy, setBusy] = useState(false);
	const requestLock = useRef(false);
	const activePublishing =
		brief?.jobs?.some((j) => ["queued", "running", "waiting_human"].includes(j.status?.state ?? "queued")) ?? false;
	const activeOperation = brief?.operation?.state === "queued" || brief?.operation?.state === "running";
	const [mediaBusy, setMediaBusy] = useState(false);
	const busy = requestBusy || activeOperation || mediaBusy;
	const [now, setNow] = useState(Date.now());
	const [error, setError] = useState("");
	const [dirty, setDirty] = useState(false);
	const [accounts, setAccounts] = useState<Record<string, string>>({});
	const [time, setTime] = useState(() => {
		const scheduled = params.get("scheduled_at");
		return scheduled && Number.isFinite(Date.parse(scheduled)) ? localDateTime(scheduled) : "";
	});
	const [deliveryMode, setDeliveryMode] = useState(params.get("scheduled_at") ? "scheduled" : "now");
	const [removeDraft, setRemoveDraft] = useState<string | null>(null);
	const [activeDraft, setActiveDraft] = useState("");
	const [inspecting, setInspecting] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [executionOpen, setExecutionOpen] = useState(false);
	const [executionTarget, setExecutionTarget] = useState<DistributionBrief | null>(null);
	const [scheduleTarget, setScheduleTarget] = useState<DistributionBrief | null>(null);
	const [scheduleTime, setScheduleTime] = useState("");
	const [scheduleOpen, setScheduleOpen] = useState(false);
	useEffect(() => {
		onPlanChange?.(!!brief);
	}, [!!brief, onPlanChange]);
	const [channelsOpen, setChannelsOpen] = useState(false);
	const creation = useRef<{ fingerprint: string; key: string } | null>(null);
	const history = useQuery({
		queryKey: [entityId, pageId, "distribution-briefs"],
		queryFn: () => smartDistribution.list(entityId),
	});
	const pages = useQuery({
		queryKey: [entityId, "distribution-materials"],
		queryFn: async () =>
			(await Promise.all([api.pages(entityId, { locale: "en" }), api.pages(entityId, { locale: "zh" })])).flat(),
	});
	const connections = useQuery({ queryKey: [entityId, "platform-tasks"], queryFn: () => api.platformTasks(entityId) });
	const source = useQuery({
		queryKey: ["distribution-source", pageId],
		queryFn: () => api.page(pageId),
		enabled: !sourceMaterial,
	});
	const material = sourceMaterial ?? source.data?.page ?? pages.data?.find((p) => p.id === pageId);
	const contentLanguage = language || material?.locale || locale;
	const languages = [
		...new Set([
			contentLanguage,
			"zh",
			"en",
			"ja",
			"ko",
			"fr",
			"de",
			"es",
			"pt",
			"it",
			"ru",
			"ar",
			"hi",
			"id",
			"vi",
			"th",
			"tr",
		]),
	];
	useEffect(() => {
		if (!connections.data) return;
		setAccounts((old) => {
			const next = { ...old };
			for (const draft of executionTarget?.drafts ?? drafts) {
				const candidates = connections.data.accounts.filter(
					(a) => a.provider_id === `pubzu:${draft.platform_id}` && a.status === "connected",
				);
				if (!next[draft.platform_id] && candidates.length === 1) next[draft.platform_id] = candidates[0].id;
				else if (
					!next[draft.platform_id] &&
					candidates.length === 0 &&
					connections.data.account_capabilities?.some((c) => c.platform_id === draft.platform_id && c.managed_available)
				)
					next[draft.platform_id] = "managed_pool";
			}
			return next;
		});
	}, [connections.data, drafts, executionTarget]);
	const registerBack = useContext(DistributionBackContext);
	const backToMaterials = useCallback(() => {
		setBrief(null);
		setDrafts([]);
		setSelected([]);
		setAccounts({});
		setTime(params.get("scheduled_at") ? localDateTime(params.get("scheduled_at")!) : "");
		setDirty(false);
		setInspecting(false);
		setSelecting(false);
		creation.current = null;
	}, [params]);
	useEffect(() => {
		registerBack?.(brief ? { go: backToMaterials, disabled: busy } : null);
		return () => registerBack?.(null);
	}, [registerBack, !!brief, busy, backToMaterials]);
	const requestedBrief = params.get("distribution_brief");
	const requested = useQuery({
		queryKey: [entityId, requestedBrief, "distribution-requested"],
		queryFn: () => smartDistribution.read(entityId, requestedBrief!),
		enabled: !!requestedBrief,
	});
	const opened = useRef<string | null>(null);
	useEffect(() => {
		if (requested.data && opened.current !== requestedBrief) {
			opened.current = requestedBrief;
			accept(requested.data);
		}
	}, [requested.data, requestedBrief]);
	const operationStatus = useQuery({
		queryKey: [entityId, brief?.id, brief?.operation?.id, "distribution-operation"],
		queryFn: () => smartDistribution.read(entityId, brief!.id),
		enabled: activeOperation || activePublishing,
		refetchInterval: activeOperation || activePublishing ? 2000 : false,
		retry: 2,
	});
	useEffect(() => {
		if (operationStatus.data && (activeOperation || activePublishing)) accept(operationStatus.data);
	}, [operationStatus.data]);
	useEffect(() => {
		if (!activeOperation) return;
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, [activeOperation]);
	const restored = useRef(false);
	useEffect(() => {
		if (requestedBrief || restored.current || !history.data) return;
		restored.current = true;
		const pending = history.data.find(
			(b) =>
				b.input.materials.some((m) => m.id === pageId) &&
				(b.operation?.state === "queued" || b.operation?.state === "running"),
		);
		if (pending && !brief && !requestBusy) accept(pending);
	}, [history.data]);
	function accept(value: DistributionBrief) {
		setBrief(value);
		setSelecting(false);
		setDrafts(value.drafts ?? []);
		setSelected((value.selected ?? value.recommendations ?? []).map((c) => c.platform_id));
		setDirty(false);
		if (value.plan?.items[0]?.scheduled_at) setTime(localDateTime(value.plan.items[0].scheduled_at));
		void history.refetch();
	}
	async function run(work: () => Promise<DistributionBrief>, openPlan = true) {
		if (requestLock.current) return;
		requestLock.current = true;
		setBusy(true);
		setError("");
		try {
			const result = await work();
			if (openPlan) accept(result);
			else {
				setScheduleTarget(result);
				setScheduleTime(result.plan?.items[0]?.scheduled_at ? localDateTime(result.plan.items[0].scheduled_at) : "");
				void history.refetch();
			}
			return true;
		} catch (e) {
			setError(e instanceof ApiError ? e.localized(locale) : t("smartDistribution.error"));
			return false;
		} finally {
			requestLock.current = false;
			setBusy(false);
		}
	}
	function recommend(channels?: ChannelSelection[]) {
		setSettingsOpen(false);
		setChannelsOpen(false);
		const input = {
			page_ids: [pageId, ...extra],
			language: contentLanguage,
			min_count: min,
			max_count: max,
			...(channels ? { channels } : {}),
		};
		const fingerprint = JSON.stringify(input);
		if (creation.current?.fingerprint !== fingerprint) creation.current = { fingerprint, key: crypto.randomUUID() };
		const request_id = creation.current.key;
		void run(() => smartDistribution.create(entityId, { ...input, request_id }));
	}
	function edit(index: number, key: string, value: string) {
		setDrafts((old) =>
			old.map((d, i) =>
				i === index
					? {
							...d,
							fields: { ...d.fields, [key]: value },
							missing_information: d.missing_information.filter((m) => m !== key && m !== `required:${key}`),
						}
					: d,
			),
		);
		setDirty(true);
	}
	const frozen = planSubmitted(brief);
	const canReschedule =
		scheduleTarget?.state === "scheduled" &&
		!!scheduleTarget.jobs?.length &&
		scheduleTarget.jobs.every((job) => job.status?.state === "queued");
	const approved = brief?.approved_revision === brief?.revision && !dirty;
	const choosing = !brief?.selected || selecting;
	const reviewing = (!approved && !frozen) || inspecting;
	const currentDraft = drafts.some((d) => d.platform_id === activeDraft) ? activeDraft : drafts[0]?.platform_id;
	const choices = brief?.selected ?? brief?.recommendations ?? [];
	function issuesFor(draft: DistributionDraft) {
		const channel = choices.find((c) => c.platform_id === draft.platform_id);
		return visibleDraftIssues(draft, channel?.platform.content_types.find((f) => f.id === draft.format)?.preview.media);
	}

	const materialAssets = [
		...(brief?.input.materials.flatMap((m) => m.assets ?? []) ?? []),
		...(brief?.input.additional_assets ?? []),
	];
	const previewFiles: Record<string, string> = {};
	for (const asset of materialAssets) {
		previewFiles[asset.file_id] = fileUrl(asset.file_id);
		previewFiles[asset.url] = fileUrl(asset.file_id);
	}
	for (const draft of drafts) {
		for (const match of JSON.stringify(draft.fields).matchAll(/ondit-file:([A-Za-z0-9_-]+)/g)) {
			previewFiles[match[1]] = fileUrl(match[1]);
		}
	}
	function previewUrl(value: string) {
		let out = value.replace(/ondit-file:([A-Za-z0-9_-]+)/g, (_, id: string) => fileUrl(id));
		for (const asset of materialAssets) out = out.split(asset.url).join(fileUrl(asset.file_id));
		return out;
	}
	const executionBrief = executionTarget ?? brief;
	const executionDrafts = executionTarget?.drafts ?? drafts;
	const executionChoices = executionTarget?.selected ?? choices;
	const canSchedule =
		executionDrafts.length > 0 &&
		executionDrafts.every(
			(d) =>
				(accounts[d.platform_id] === "managed_pool"
					? !!connections.data?.account_capabilities?.some(
							(c) => c.platform_id === d.platform_id && c.managed_available,
						)
					: !!connections.data?.accounts.some(
							(a) =>
								a.id === accounts[d.platform_id] &&
								a.provider_id === `pubzu:${d.platform_id}` &&
								a.status === "connected",
						)) && !!executionChoices.find((c) => c.platform_id === d.platform_id)?.platform.workflow_id,
		);
	function submitPlan(immediate: boolean) {
		const target = executionBrief;
		const fromHistory = !!executionTarget;
		if (!target || planSubmitted(target) || !canSchedule || busy || !editor) return;
		const scheduled = immediate
			? new Date().toISOString()
			: Number.isFinite(Date.parse(time)) && Date.parse(time) > Date.now()
				? new Date(time).toISOString()
				: null;
		if (!scheduled) return;
		void run(async () => {
			const reviewed = (fromHistory ? target.approved_revision === target.revision : approved)
				? target
				: await smartDistribution.change(entityId, target.id, "review", {
						revision: target.revision,
						drafts: executionDrafts,
						approve: true,
					});
			// Retain the confirmed revision if plan submission fails.
			if (fromHistory) setExecutionTarget(reviewed);
			else accept(reviewed);
			let result: DistributionBrief;
			try {
				result = await smartDistribution.change(entityId, target.id, "plan", {
					revision: reviewed.revision,
					items: executionDrafts.map((d) => ({
						platform_id: d.platform_id,
						...(accounts[d.platform_id] === "managed_pool"
							? { account_source: "managed_pool" }
							: { account_source: "customer", account_id: accounts[d.platform_id] }),
						scheduled_at: scheduled,
					})),
				});
			} catch (error) {
				// Reload a frozen submission instead of letting a retry pick a new timestamp.
				const current = await smartDistribution.read(entityId, target.id).catch(() => null);
				if (current) {
					accept(current);
					setExecutionTarget(null);
					setExecutionOpen(false);
				}
				throw error;
			}

			setExecutionOpen(false);
			setExecutionTarget(null);
			return result;
		}, !fromHistory);
	}
	function startDelivery() {
		if (frozen || busy || !editor) return;
		setExecutionOpen(true);
	}

	function planDate(plan: DistributionBrief) {
		const times = [...new Set((plan.plan?.items ?? []).map((item) => item.scheduled_at))]
			.filter((value) => Number.isFinite(Date.parse(value)))
			.sort((a, b) => Date.parse(a) - Date.parse(b));
		if (times.length) {
			return times.length === 1
				? dateTime(locale, times[0])
				: `${dateTime(locale, times[0])} – ${dateTime(locale, times[times.length - 1])}`;
		}
		return `${pick(locale, ["Unscheduled", "未排期"])}${plan.created_at ? ` · ${pick(locale, ["Created", "创建于"])} ${dateTime(locale, plan.created_at)}` : ""}`;
	}
	const timingControl = (
		<div className="smart-plan-timing">
			<Label htmlFor="distribution-time">{pick(locale, ["Scheduled time", "分发时间"])}</Label>
			<Input
				id="distribution-time"
				type="datetime-local"
				value={time}
				disabled={!editor || busy}
				onChange={(e) => setTime(e.target.value)}
			/>
		</div>
	);
	return (
		<section className="smart-distribution" aria-label={t("smartDistribution.heading")}>
			{!brief && (
				<div className="smart-fields smart-preparation">
					{sourceControl || (
						<div className="smart-source-title">
							<small>{t("smartDistribution.material")}</small>
							<strong>{material?.title ?? t("smartDistribution.material")}</strong>
						</div>
					)}
					{!material &&
						(source.error ? (
							<p role="alert">
								{source.error instanceof ApiError ? source.error.localized(locale) : t("smartDistribution.error")}{" "}
								<Button variant="ghost" onClick={() => void source.refetch()}>
									{t("state.retry")}
								</Button>
							</p>
						) : (
							<p>{t("smartDistribution.working")}</p>
						))}
					<ChipModal
						open={settingsOpen}
						onOpenChange={setSettingsOpen}
						srTitle={t("smartDistribution.settings")}
						dismissDisabled={busy}
					>
						<ChipModalHeader onClose={() => setSettingsOpen(false)} closeAriaLabel={t("common.close")}>
							{t("smartDistribution.settings")}
						</ChipModalHeader>
						<ChipModalBody>
							<div className="smart-settings">
								<div className="smart-setting-grid">
									<Label>
										{t("smartDistribution.language")}
										<NativeSelect
											value={contentLanguage}
											onChange={(e) => setLanguage(e.target.value)}
											disabled={!editor || busy}
										>
											{languages.map((code) => (
												<option key={code} value={code}>
													{languageName(locale, code)}
												</option>
											))}
										</NativeSelect>
									</Label>
									<Label>
										{t("smartDistribution.minimum")}
										<Input
											type="number"
											min={1}
											max={30}
											disabled={!editor || busy}
											value={min}
											onChange={(e) => setMin(Number(e.target.value))}
										/>
									</Label>
									<Label>
										{t("smartDistribution.maximum")}
										<Input
											type="number"
											min={min}
											max={30}
											disabled={!editor || busy}
											value={max}
											onChange={(e) => setMax(Number(e.target.value))}
										/>
									</Label>
								</div>
								<Label>{t("smartDistribution.additional")}</Label>
								<div className="smart-material-picker">
									<SearchSelect
										value={adding}
										onChange={setAdding}
										options={(pages.data ?? [])
											.filter((p) => p.id !== pageId && !extra.includes(p.id))
											.map((p) => ({ value: p.id, label: p.title }))}
										label={t("smartDistribution.additional")}
										placeholder={t("smartDistribution.additional")}
										searchLabel={t("table.search")}
										emptyLabel={t("table.noMatch")}
									/>
									<Button
										variant="outline"
										disabled={!adding || extra.length >= 9 || !editor}
										onClick={() => {
											setExtra([...extra, adding]);
											setAdding("");
										}}
									>
										{t("smartDistribution.add")}
									</Button>
								</div>
								{extra.map((id) => (
									<div className="smart-material-item" key={id}>
										<span>{pages.data?.find((p) => p.id === id)?.title}</span>
										<Button
											variant="ghost"
											size="icon"
											disabled={!editor || busy}
											aria-label={t("smartDistribution.remove")}
											title={t("smartDistribution.remove")}
											onClick={() => setExtra(extra.filter((p) => p !== id))}
										>
											<Trash2 aria-hidden="true" />
										</Button>
									</div>
								))}
								<Button
									variant="primary"
									disabled={!editor || busy || !material || disabledRecommendation || min < 1 || max < min || max > 30}
									onClick={() => recommend()}
								>
									{t("smartDistribution.recommend")}
								</Button>
							</div>
						</ChipModalBody>
					</ChipModal>
					<div className="smart-preparation-toolbar">
						<div className="smart-channel-actions">
							<Button
								variant="primary"
								disabled={!editor || busy || disabledRecommendation || !material || !contentLanguage}
								onClick={() => setSettingsOpen(true)}
							>
								{t(busy ? "smartDistribution.working" : "smartDistribution.recommend")}
							</Button>
							<Button
								variant="outline"
								disabled={!editor || busy || !material || disabledRecommendation}
								onClick={() => setChannelsOpen(true)}
							>
								{pick(locale, ["Add channels", "添加渠道"])}
							</Button>
						</div>
					</div>
					<ChannelPicker
						entityId={entityId}
						open={channelsOpen}
						onClose={() => setChannelsOpen(false)}
						onConfirm={recommend}
					/>
				</div>
			)}
			{brief?.operation && (activeOperation || brief.operation.state === "failed") && (
				<div className="smart-operation" role="status">
					<strong>{t(`smartDistribution.stage.${brief.operation.stage}`)}</strong>
					<Link
						to={`/w/${encodeURIComponent(entityId)}/tasks/distribution?${new URLSearchParams({ operation: brief.operation.id })}`}
					>
						{pick(locale, ["View task", "查看任务"])} #{brief.operation.id.slice(-8)}
					</Link>
					{activeOperation && (
						<small>
							{pick(locale, ["Elapsed", "已耗时"])}{" "}
							{Math.max(0, Math.floor((now - Date.parse(brief.operation.created_at)) / 1000))}{" "}
							{pick(locale, ["seconds", "秒"])} ·{" "}
							{pick(locale, ["You can close this panel and return later.", "可关闭面板，稍后回来查看。"])}{" "}
						</small>
					)}
					{brief.operation.state === "failed" && (
						<p role="alert">
							{lookup(
								locale,
								`errors.distribution_${brief.operation.error === "invalid_distribution_input" ? "invalid_input" : brief.operation.error}`,
							) || t("smartDistribution.error")}
						</p>
					)}
					{operationStatus.error && (
						<p role="alert">
							{pick(locale, [
								"Progress refresh failed. The task may still be running; refresh its status before starting again.",
								"进度刷新失败，任务可能仍在执行，请先刷新状态确认结果。",
							])}
						</p>
					)}
				</div>
			)}
			{brief && (
				<ChipConfirmModal
					open={!!removeDraft}
					onOpenChange={(open) => {
						if (!open) setRemoveDraft(null);
					}}
					title={pick(locale, ["Remove channel", "删除渠道"])}
					text={pick(locale, [
						"Remove this channel and its draft from the plan? Other channel content will remain. Unsaved edits will be discarded.",
						"从计划中删除此渠道及其成稿？其他渠道的已保存内容会保留，未保存的修改将丢弃。",
					])}
					dismissLabel={t("common.cancel")}
					confirm={{
						label: pick(locale, ["Remove", "删除"]),
						variant: "destructive",
						pending: busy,
						disabled: !editor || busy,
						onClick: () => {
							if (removeDraft)
								void run(() =>
									smartDistribution.change(entityId, brief.id, "remove_channel", {
										revision: brief.revision,
										platform_id: removeDraft,
									}),
								).then(() => setRemoveDraft(null));
						},
					}}
				/>
			)}
			{brief && (
				<>
					{!registerBack && (
						<Button
							variant="ghost"
							size="icon"
							disabled={busy}
							onClick={backToMaterials}
							aria-label={pick(locale, ["Back", "返回"])}
						>
							<ArrowLeft size={16} />
						</Button>
					)}
					<div className="smart-plan-toolbar">
						<div>
							<strong title={`${pick(locale, ["Plan ID", "计划编号"])}: ${brief.id}`}>
								{pick(locale, ["Distribution plan", "分发计划"])} · {planDate(brief)}
								{frozen && (
									<span className="ml-2 hint" role="status">
										{t(`smartDistribution.state.${distributionPlanState(brief)}`)}
									</span>
								)}
							</strong>
						</div>
						<div className="smart-plan-actions">
							{(brief.state === "submitting" || brief.pending_plan) && (
								<Button
									variant="outline"
									disabled={!editor || busy}
									onClick={() => void run(() => smartDistribution.change(entityId, brief.id, "recover", {}))}
								>
									{pick(locale, ["Continue submission", "继续提交"])}
								</Button>
							)}
							{frozen && distributionPlanState(brief) === "scheduled" && (
								<Button
									variant="outline"
									disabled={!editor || busy}
									onClick={() => {
										setScheduleTarget(brief);
										setScheduleTime(
											brief.plan?.items[0]?.scheduled_at ? localDateTime(brief.plan.items[0].scheduled_at) : "",
										);
										setScheduleOpen(true);
									}}
								>
									{pick(locale, ["Edit schedule", "修改排期"])}
								</Button>
							)}
							<Button asChild variant="ghost">
								<Link
									onClick={onEditMaterial}
									to={`/w/${encodeURIComponent(entityId)}/agent/home?${new URLSearchParams({ article: pageId, resource: pageId })}`}
								>
									{pick(locale, ["Edit article", "编辑文章"])}
								</Link>
							</Button>
							{brief.selected && !frozen && !approved && (
								<Button variant="ghost" disabled={!editor || busy} onClick={() => setSelecting(true)}>
									{pick(locale, ["Adjust channels", "调整渠道"])}
								</Button>
							)}
							<Button
								variant="ghost"
								size="icon"
								aria-label={t("smartDistribution.refresh")}
								title={t("smartDistribution.refresh")}
								disabled={requestBusy}
								onClick={() => void run(() => smartDistribution.read(entityId, brief.id))}
							>
								<RefreshCw size={16} className={requestBusy ? "animate-spin" : undefined} />
							</Button>
						</div>
					</div>
					{choosing && brief.analysis?.purpose && <p>{brief.analysis.purpose}</p>}
					{brief.shortfall_reason && <p className="hint">{brief.shortfall_reason}</p>}
					{(!brief.selected || selecting) && !frozen && (
						<>
							<div className="smart-choices">
								{brief.recommendations?.map((c) => (
									<Label key={c.platform_id} className="smart-choice">
										<input
											type="checkbox"
											checked={selected.includes(c.platform_id)}
											disabled={!editor || busy}
											onChange={(e) =>
												setSelected(
													e.target.checked
														? [...selected, c.platform_id]
														: selected.filter((id) => id !== c.platform_id),
												)
											}
										/>
										<span>
											<strong>{c.platform.name}</strong>
											<p>{c.reason}</p>
											<small>
												{(c.platform.languages ?? []).join(locale === "zh" ? "；" : "; ")} ·{" "}
												{t(
													!c.platform.workflow_id
														? "smartDistribution.pendingWorkflow"
														: c.platform.verified
															? "smartDistribution.ready"
															: "smartDistribution.unverified",
												)}
											</small>
										</span>
									</Label>
								))}
							</div>
							<Button
								variant="primary"
								disabled={!editor || busy || !selected.length}
								onClick={() =>
									void run(() =>
										smartDistribution.change(entityId, brief.id, "adapt", {
											revision: brief.revision,
											platform_ids: selected,
										}),
									)
								}
							>
								{t(busy ? "smartDistribution.working" : "smartDistribution.adapt")}
							</Button>
						</>
					)}
					{drafts.length > 0 && (
						<>
							{(approved || frozen) && (
								<Button variant="ghost" onClick={() => setInspecting(!inspecting)}>
									{t(inspecting ? "smartDistribution.hideContent" : "smartDistribution.viewContent")}
								</Button>
							)}
							{reviewing && (
								<>
									<nav className="smart-draft-nav" aria-label={t("smartDistribution.reviewPlatforms")}>
										{drafts.map((d) => (
											<div className="smart-draft-tab" key={d.platform_id}>
												<Button
													key={d.platform_id}
													variant={currentDraft === d.platform_id ? "outline" : "ghost"}
													aria-pressed={currentDraft === d.platform_id}
													onClick={() => setActiveDraft(d.platform_id)}
												>
													{choices.find((c) => c.platform_id === d.platform_id)?.platform.name ?? d.platform_id}
													{issuesFor(d).length > 0 && (
														<span className="smart-needs-attention">{pick(locale, ["Needs input", "待补充"])}</span>
													)}
												</Button>
												{!frozen && (
													<Button
														variant="ghost"
														className="text-destructive smart-channel-remove"
														disabled={!editor || busy}
														aria-label={pick(locale, [
															`Remove ${choices.find((c) => c.platform_id === d.platform_id)?.platform.name ?? d.platform_id}`,
															`删除${choices.find((c) => c.platform_id === d.platform_id)?.platform.name ?? d.platform_id}`,
														])}
														onClick={() => setRemoveDraft(d.platform_id)}
													>
														<Trash2 size={14} />
													</Button>
												)}
											</div>
										))}
									</nav>
									{drafts.map((d, index) => {
										if (d.platform_id !== currentDraft) return null;
										const c = choices.find((c) => c.platform_id === d.platform_id);
										const model = c?.platform.content_types.find((f) => f.id === d.format);
										const bodyField = model?.preview.fields.find((f) => f.key === "body" && f.kind === "textarea");
										const titleField = model?.preview.fields.find((f) => f.key === "title");
										const sheetField = (f: Field) => ({
											key: f.key,
											label: `${f.label ? pick(locale, f.label) : f.key}${fieldRequired(f, d.fields) ? " *" : ""}`,
											value: d.fields[f.key] ?? "",
											onChange: (value: string) => edit(index, f.key, value),
										});
										return (
											<article className="smart-draft" key={d.platform_id}>
												<h4>
													{c?.platform.name ?? d.platform_id} · {model ? pick(locale, model.label) : d.format}
												</h4>
												{issuesFor(d).length > 0 && (
													<div className="smart-draft-issues" role="status">
														{pick(locale, ["Needs input", "待补充"])}:{" "}
														{issuesFor(d)
															.filter(
																(issue) =>
																	issue !== "media_asset_required" ||
																	!d.missing_information.some((detail) => /图片|视频|image|video/i.test(detail)),
															)
															.map((issue) => {
																const key = issue.split(":")[1];
																const field = model?.preview.fields.find((f) => f.key === key);
																if (issue === "media_asset_required")
																	return pick(locale, [
																		"Add the images or videos required by this content format.",
																		"缺少该内容形式要求的图片或视频素材。",
																	]);
																if (issue.startsWith("required:"))
																	return `${field?.label ? pick(locale, field.label) : key} · ${pick(locale, ["Required", "必填"])}`;
																return field ? `${field.label ? pick(locale, field.label) : field.key}` : issue;
															})
															.join(", ")}
														{!frozen &&
															d.missing_information.length > 0 &&
															!(model?.preview.media?.required && !d.media.length) && (
																<Button
																	variant="ghost"
																	disabled={!editor || busy}
																	onClick={() => {
																		setDrafts((old) =>
																			old.map((value, i) =>
																				i === index ? { ...value, missing_information: [] } : value,
																			),
																		);
																		setDirty(true);
																	}}
																>
																	{t("smartDistribution.resolved")}
																</Button>
															)}
													</div>
												)}

												<div className="smart-draft-columns">
													<div className="smart-fields">
														{model?.preview.media && (
															<SourceMediaField
																brandId={entityId}
																onBusyChange={setMediaBusy}
																assets={materialAssets}
																value={d.media}
																rule={model.preview.media}
																disabled={!editor || busy || frozen}
																onEditMaterial={onEditMaterial}
																editUrl={`/w/${encodeURIComponent(entityId)}/agent/home?${new URLSearchParams({ article: pageId, resource: pageId })}`}
																onChange={(media) => {
																	setDrafts((old) =>
																		old.map((draft, i) => (i === index ? { ...draft, media } : draft)),
																	);
																	setDirty(true);
																}}
															/>
														)}
														{bodyField && (
															<div className="smart-field-body">
																<ArticleSheet
																	disabled={!editor || busy || frozen}
																	title={titleField && sheetField(titleField)}
																	body={sheetField(bodyField)}
																	plainBody={!["markdown", "rich_text"].includes(bodyField.format ?? "")}
																	actions={
																		<div className="row-actions publisher-sheet-counts">
																			{[titleField, bodyField].map((f) =>
																				f?.max_length != null ? (
																					<span key={f.key} className="publisher-count">
																						{f.label ? pick(locale, f.label) : f.key}{" "}
																						{fieldLength(f, d.fields[f.key] ?? "")} / {f.max_length}
																					</span>
																				) : null,
																			)}
																		</div>
																	}
																/>
															</div>
														)}
														{model?.preview.fields
															.filter((f) => !bodyField || !["title", "body"].includes(f.key))
															.map((f) => (
																<div
																	key={f.key}
																	className={
																		["body", "text", "description", "content", "script"].includes(f.key)
																			? "smart-field-body"
																			: undefined
																	}
																>
																	<PublishingField
																		field={f}
																		fields={d.fields}
																		disabled={!editor || busy || frozen}
																		onChange={(value) => edit(index, f.key, value)}
																		imageControl={
																			<SourceImageField
																				brandId={entityId}
																				onBusyChange={setMediaBusy}
																				value={d.fields[f.key] ?? ""}
																				assets={materialAssets}
																				disabled={!editor || busy || frozen}
																				onChange={(url) => edit(index, f.key, url)}
																			/>
																		}
																	/>
																</div>
															))}
													</div>
													<div className="smart-preview" data-expanded={previewOpen}>
														<Button
															className="smart-preview-toggle"
															variant="ghost"
															aria-expanded={previewOpen}
															onClick={() => setPreviewOpen(!previewOpen)}
														>
															{t(previewOpen ? "smartDistribution.hideContent" : "editor.preview")}
														</Button>
														<div className="smart-preview-content">
															{model && (
																<PlatformPreview
																	platform={{
																		id: d.platform_id,
																		name: c?.platform.name ?? d.platform_id,
																		preview: {
																			...model.preview,
																			fields: model.preview.fields.map((f) => ({
																				...f,
																				label: f.label ?? [f.key, f.key],
																			})),
																			media: model.preview.media ?? null,
																		},
																	}}
																	resolvedFiles={previewFiles}
																	resolvedMedia={d.media.map((m) => ({
																		url: previewUrl(m.url),
																		alt: "",
																		kind: m.type === "video" || m.mime?.startsWith("video/") ? "video" : "image",
																	}))}
																	payload={{
																		fields: d.fields,
																		media: d.media.map((m) => ({ url: m.url, alt: "" })),
																	}}
																/>
															)}
														</div>
													</div>
												</div>
												{!c?.platform.workflow_id && <p className="hint">{t("smartDistribution.pendingWorkflow")}</p>}
											</article>
										);
									})}
								</>
							)}
							{!frozen && (
								<>
									<div className="smart-row smart-review-actions">
										<Button
											variant="outline"
											disabled={!editor || busy}
											onClick={() =>
												void run(() =>
													smartDistribution.change(entityId, brief.id, "review", {
														revision: brief.revision,
														drafts,
														approve: false,
													}),
												)
											}
										>
											{t("smartDistribution.save")}
										</Button>
										<Button variant="primary" disabled={!editor || busy || !drafts.length} onClick={startDelivery}>
											{busy ? t("smartDistribution.working") : pick(locale, ["Distribute", "分发"])}
										</Button>
									</div>
								</>
							)}
							{brief.jobs?.map((j) => (
								<p key={j.job_id}>
									{choices.find((c) => c.platform_id === j.platform_id)?.platform.name ?? j.platform_id}:{" "}
									{t(`smartDistribution.state.${j.status?.state ?? "queued"}`)}
									{brief.plan?.items.find((item) => item.platform_id === j.platform_id)?.scheduled_at && (
										<span className="ml-2 hint">
											{dateTime(
												locale,
												brief.plan.items.find((item) => item.platform_id === j.platform_id)!.scheduled_at,
											)}
										</span>
									)}
									<Link
										className="ml-2 text-primary"
										to={`/w/${encodeURIComponent(entityId)}/tasks/distribution?${new URLSearchParams({ operation: j.job_id })}`}
									>
										{pick(locale, ["View task", "查看任务"])} #{j.job_id.slice(-8)}
									</Link>
									{editor &&
										j.status?.state === "waiting_human" &&
										brief.plan?.items.find((item) => item.platform_id === j.platform_id)?.account_source ===
											"customer" && (
											<ConnectAccount
												entityId={entityId}
												platform={{
													id: j.platform_id,
													name: choices.find((c) => c.platform_id === j.platform_id)?.platform.name ?? j.platform_id,
												}}
												taskJobId={j.job_id}
												variant="outline"
											/>
										)}
									{editor && ["queued", "running", "waiting_human"].includes(j.status?.state ?? "queued") && (
										<Button
											variant="outline"
											disabled={busy}
											onClick={() =>
												void run(() =>
													smartDistribution.change(entityId, brief.id, "cancel", {
														revision: brief.revision,
														job_id: j.job_id,
													}),
												)
											}
										>
											{t("smartDistribution.stop")}
										</Button>
									)}
									{j.status?.published_url && (
										<>
											{" "}
											·{" "}
											<a href={j.status.published_url} target="_blank" rel="noreferrer">
												{t("smartDistribution.open")}
											</a>
										</>
									)}
								</p>
							))}
						</>
					)}
				</>
			)}

			<ChipModal
				open={scheduleOpen && !!scheduleTarget}
				onOpenChange={setScheduleOpen}
				srTitle={pick(locale, ["Edit schedule", "修改排期"])}
				dismissDisabled={busy}
			>
				<ChipModalHeader onClose={() => setScheduleOpen(false)} closeAriaLabel={t("common.close")}>
					{pick(locale, ["Edit schedule", "修改排期"])}
				</ChipModalHeader>
				<ChipModalBody>
					<div className="smart-plan-timing">
						<Label htmlFor="schedule-edit-time">
							{pick(locale, ["Scheduled time (optional)", "排期时间（选填）"])}
						</Label>
						<Input
							id="schedule-edit-time"
							type="datetime-local"
							value={scheduleTime}
							disabled={!editor || busy}
							onChange={(e) => setScheduleTime(e.target.value)}
						/>
					</div>
					<p className="hint">
						{pick(locale, [
							"Only queued tasks can be rescheduled. All channels in this plan will use this time.",
							"仅等待执行的任务可以修改排期，计划中的所有渠道将使用此时间。",
						])}
					</p>
					{!canReschedule && (
						<p role="alert">
							{pick(locale, [
								"This plan is no longer queued. Its schedule cannot be changed.",
								"该计划已不处于等待执行状态，无法修改排期。",
							])}
						</p>
					)}
					{error && <p role="alert">{error}</p>}
					<Button
						variant="primary"
						disabled={
							!canReschedule ||
							!editor ||
							busy ||
							!scheduleTime ||
							Date.parse(scheduleTime) <= Date.now() ||
							!Number.isFinite(Date.parse(scheduleTime))
						}
						onClick={async () => {
							if (!scheduleTarget) return;
							if (
								await run(
									() =>
										smartDistribution.change(entityId, scheduleTarget.id, "reschedule", {
											revision: scheduleTarget.revision,
											expected_plan: scheduleTarget.plan,
											scheduled_at: new Date(scheduleTime).toISOString(),
										}),
									brief?.id === scheduleTarget.id,
								)
							)
								setScheduleOpen(false);
						}}
					>
						{pick(locale, ["Save schedule", "保存排期"])}
					</Button>
				</ChipModalBody>
			</ChipModal>

			{error && <p role="alert">{error}</p>}
			{history.error && (
				<p role="alert">
					{history.error instanceof ApiError ? history.error.localized(locale) : t("smartDistribution.error")}
					<Button variant="ghost" onClick={() => void history.refetch()} disabled={history.isFetching}>
						{t("state.retry")}
					</Button>
				</p>
			)}
			{executionBrief && executionDrafts.length > 0 && (
				<ChipModal
					open={executionOpen}
					onOpenChange={(open) => {
						setExecutionOpen(open);
						if (!open) setExecutionTarget(null);
					}}
					size="xl"
					srTitle={pick(locale, ["Distribution settings", "分发设置"])}
					dismissDisabled={busy}
				>
					<ChipModalHeader
						onClose={() => {
							setExecutionOpen(false);
							setExecutionTarget(null);
						}}
						closeAriaLabel={t("common.close")}
					>
						{pick(locale, ["Distribution settings", "分发设置"])}
					</ChipModalHeader>
					<ChipModalBody>
						<section className="distribution-settings">
							{error && <p role="alert">{error}</p>}
							<div className="distribution-settings-heading">
								<h3>{pick(locale, ["Publishing accounts", "发布账号"])}</h3>
								<span className="hint">
									{executionDrafts.length} {pick(locale, ["channels", "个渠道"])}
								</span>
							</div>
							<div className="distribution-account-columns" aria-hidden="true">
								<span>{pick(locale, ["Channel", "渠道"])}</span>
								<span>{pick(locale, ["Account source", "账号来源"])}</span>
								<span>{pick(locale, ["Publishing account", "发布账号"])}</span>
							</div>
							{executionDrafts.map((d) => (
								<div key={d.platform_id} className="distribution-account-row">
									<div className="distribution-channel">
										<strong>
											{executionChoices.find((c) => c.platform_id === d.platform_id)?.platform.name ?? d.platform_id}
										</strong>
										{!executionChoices.find((c) => c.platform_id === d.platform_id)?.platform.workflow_id && (
											<small className="distribution-blocker">{t("smartDistribution.pendingWorkflow")}</small>
										)}
									</div>
									<div className="distribution-account-controls">
										<Label className="distribution-account-label">
											<span className="distribution-responsive-label">
												{pick(locale, ["Account source", "账号来源"])}
											</span>
											<NativeSelect
												value={accounts[d.platform_id] === "managed_pool" ? "managed_pool" : "customer"}
												disabled={!editor || busy}
												onChange={(e) =>
													setAccounts({
														...accounts,
														[d.platform_id]: e.target.value === "managed_pool" ? "managed_pool" : "",
													})
												}
											>
												<option value="customer">{pick(locale, ["My account", "我的账号"])}</option>
												<option
													value="managed_pool"
													disabled={
														!connections.data?.account_capabilities?.some(
															(c) => c.platform_id === d.platform_id && c.managed_available,
														)
													}
												>
													{pick(locale, ["Platform account", "平台账号"])}
												</option>
											</NativeSelect>
										</Label>
										{accounts[d.platform_id] === "managed_pool" ? (
											<p className="hint">{pick(locale, ["Automatically assigned", "系统自动分配"])}</p>
										) : (
											<div className="distribution-personal-account">
												<Label className="distribution-account-label">
													<span className="distribution-responsive-label">
														{pick(locale, ["My connected account", "我的已连接账号"])}
													</span>
													<NativeSelect
														value={accounts[d.platform_id] ?? ""}
														disabled={!editor || busy}
														onChange={(e) => setAccounts({ ...accounts, [d.platform_id]: e.target.value })}
													>
														<option value="">
															{connections.data?.accounts.some(
																(a) => a.provider_id === `pubzu:${d.platform_id}` && a.status === "connected",
															)
																? t("smartDistribution.choose")
																: pick(locale, ["No connected account", "尚未连接账号"])}
														</option>
														{connections.data?.accounts
															.filter((a) => a.provider_id === `pubzu:${d.platform_id}` && a.status === "connected")
															.map((a) => (
																<option key={a.id} value={a.id}>
																	{a.name}
																</option>
															))}
													</NativeSelect>
												</Label>
												<ConnectAccount
													entityId={entityId}
													platform={{
														id: d.platform_id,
														name:
															executionChoices.find((c) => c.platform_id === d.platform_id)?.platform.name ??
															d.platform_id,
														credential_provider: `pubzu:${d.platform_id}`,
														credential_kind: "pubzu",
													}}
													size="sm"
													variant="ghost"
													disabled={busy}
												/>
											</div>
										)}
									</div>
								</div>
							))}
							<div className="distribution-delivery-options">
								<Label>
									{pick(locale, ["Distribution mode", "分发方式"])}
									<NativeSelect
										value={deliveryMode}
										onChange={(e) => setDeliveryMode(e.target.value)}
										disabled={!editor || busy}
									>
										<option value="now">{pick(locale, ["Immediately", "立即分发"])}</option>
										<option value="scheduled">{pick(locale, ["Schedule", "排期"])}</option>
									</NativeSelect>
								</Label>
								{deliveryMode === "scheduled" && timingControl}
							</div>
							{deliveryMode === "scheduled" && time && Date.parse(time) <= Date.now() && (
								<p role="alert">{pick(locale, ["Choose a future time.", "请选择未来的分发时间。"])}</p>
							)}
							<p className="hint distribution-confirmation">
								{pick(locale, [
									"Distributing confirms the current content for all channels.",
									"分发即确认所有渠道的当前内容。",
								])}
							</p>
						</section>
					</ChipModalBody>
					<ChipModalFooter
						onCancel={() => {
							setExecutionOpen(false);
							setExecutionTarget(null);
						}}
						cancelLabel={pick(locale, ["Cancel", "取消"])}
						cancelDisabled={busy}
						defaultAction="none"
						primaryAction={{
							label: busy ? pick(locale, ["Submitting…", "提交中…"]) : pick(locale, ["Distribute", "分发"]),
							disabled:
								!editor ||
								busy ||
								!canSchedule ||
								(deliveryMode === "scheduled" &&
									(!time || !Number.isFinite(Date.parse(time)) || Date.parse(time) <= Date.now())),
							disabledTooltip: !canSchedule
								? pick(locale, [
										"Connect an account and ensure each channel has a publishing workflow.",
										"请连接账号，并确保各渠道有可用的发布流程。",
									])
								: undefined,
							onClick: () => {
								void submitPlan(deliveryMode === "now");
							},
						}}
					/>
				</ChipModal>
			)}
			{!brief && (history.data ?? []).filter((b) => b.input.materials.some((m) => m.id === pageId)).length > 0 && (
				<section className="smart-history" aria-label={t("smartDistribution.history")}>
					<h3>{t("smartDistribution.history")}</h3>
					{history.data
						?.filter((b) => b.input.materials.some((m) => m.id === pageId))
						.map((b) => (
							<div className="smart-row" key={b.id}>
								<div className="smart-history-summary">
									<strong title={`${pick(locale, ["Plan ID", "计划编号"])}: ${b.id}`}>{planDate(b)}</strong>
									<small>
										{languageName(locale, b.input.language)} · {pick(locale, ["Target", "目标"])}{" "}
										{b.input.min_count === b.input.max_count
											? b.input.min_count
											: `${b.input.min_count}–${b.input.max_count}`}{" "}
										{pick(locale, ["channels", "个渠道"])}
									</small>
									<small>
										{b.operation?.state === "failed"
											? t("smartDistribution.stage.failed")
											: t(`smartDistribution.state.${distributionPlanState(b)}`)}
									</small>
								</div>
								{!planSubmitted(b) && ["drafted", "approved"].includes(b.state) && (
									<Button
										variant="outline"
										disabled={!editor || busy}
										onClick={(event) => {
											event.stopPropagation();
											void run(async () => {
												const current = await smartDistribution.read(entityId, b.id);
												if (!planSubmitted(current) && current.drafts?.length) {
													setExecutionTarget(current);
													setTime("");
													setAccounts({});
													setDeliveryMode(params.get("scheduled_at") ? "scheduled" : "now");
													setExecutionOpen(true);
												}
												return current;
											}, false);
										}}
									>
										{pick(locale, ["Distribute", "分发"])}
									</Button>
								)}
								{distributionPlanState(b) === "scheduled" && (
									<Button
										variant="ghost"
										disabled={!editor || busy}
										onClick={async () => {
											if (await run(() => smartDistribution.read(entityId, b.id), false)) setScheduleOpen(true);
										}}
									>
										{pick(locale, ["Edit schedule", "修改时间"])}
									</Button>
								)}
								<Button
									variant="ghost"
									disabled={busy}
									onClick={() => void run(() => smartDistribution.read(entityId, b.id))}
								>
									{t("smartDistribution.open")}
								</Button>
							</div>
						))}
				</section>
			)}
		</section>
	);
}
